import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from "lucide-react";
import { PRINT_TEMPLATES, type PrintCheckResult } from "@erc/shared";
import { apiClient } from "../../lib/api";

const STORE_KEY = "erc:print-check:template";

// "Baskiya hazir mi?" — tasarim gorseli secilen urunun baski sablonuna gore
// denetlenir (sunucu: lib/print-check.ts). Urun tipi analizden gelir; secim
// tarayicida hatirlanir.
export function PrintCheckPanel({ imageUrl, productTypeKey }: { imageUrl: string; productTypeKey?: string | null }) {
  const initial = (() => {
    const byType = productTypeKey ? PRINT_TEMPLATES.find((t) => t.productTypeKey === productTypeKey)?.key : undefined;
    if (byType) return byType;
    try { return localStorage.getItem(STORE_KEY) || PRINT_TEMPLATES[0].key; } catch { return PRINT_TEMPLATES[0].key; }
  })();
  const [templateKey, setTemplateKey] = useState(initial);
  const template = PRINT_TEMPLATES.find((t) => t.key === templateKey) ?? PRINT_TEMPLATES[0];
  const [variantId, setVariantId] = useState(template.variants[0].id);
  const [result, setResult] = useState<PrintCheckResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBusy(true); setError(null);
    apiClient.printCheck({ imageUrl, templateKey: template.key, variantId })
      .then((r) => { if (!cancelled) setResult(r); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Denetlenemedi"); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [imageUrl, template.key, variantId]);

  const pickTemplate = (key: string) => {
    const t = PRINT_TEMPLATES.find((x) => x.key === key) ?? PRINT_TEMPLATES[0];
    setTemplateKey(t.key);
    setVariantId(t.variants[0].id);
    try { localStorage.setItem(STORE_KEY, t.key); } catch { /* yoksay */ }
  };

  const errors = result?.issues.filter((i) => i.level === "error").length ?? 0;
  const warns = result?.issues.filter((i) => i.level === "warn").length ?? 0;
  const tone = errors ? "border-red-200 bg-red-50" : warns ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50";

  return (
    <div className={`mt-2 rounded-lg border p-2 text-xs ${result ? tone : "border-gray-200 bg-gray-50"}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-medium text-gray-700">Baskıya hazır mı</span>
        <select value={template.key} onChange={(e) => pickTemplate(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-1 py-0.5">
          {PRINT_TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        {template.variants.length > 1 && (
          <select value={variantId} onChange={(e) => setVariantId(e.target.value)} className="rounded-lg border border-gray-200 bg-white px-1 py-0.5">
            {template.variants.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
          </select>
        )}
        {busy && <Loader2 className="h-3 w-3 animate-spin text-gray-500" />}
      </div>
      {error && <p className="mt-1 text-red-700">{error}</p>}
      {result && !busy && (
        <div className="mt-1 space-y-0.5">
          <p className="text-gray-600">{result.width}×{result.height} px · {result.effectiveDpi} DPI (gereken {template.dpi})</p>
          {result.issues.length === 0 && (
            <p className="flex items-center gap-1 font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Sorun bulunmadı</p>
          )}
          {result.colorFit && result.colorFit.length > 0 && (
            <div className="pt-0.5">
              <p className="text-gray-600">Ürün rengi uyumu (tasarımın görünen kısmı):</p>
              <div className="mt-0.5 flex flex-wrap gap-1">
                {result.colorFit.map((c) => (
                  <span key={c.name} title={`${c.name}: tasarımın %${c.visible}'i görünür`}
                    className={`flex items-center gap-1 rounded-full border px-1.5 py-0.5 ${c.visible >= 90 ? "border-emerald-200 bg-white text-emerald-700" : c.visible >= 70 ? "border-amber-200 bg-white text-amber-700" : "border-red-200 bg-white text-red-600 line-through"}`}>
                    <span className="h-2.5 w-2.5 rounded-full border border-gray-300" style={{ background: c.hex }} />
                    {c.name} %{c.visible}
                  </span>
                ))}
              </div>
            </div>
          )}
          {result.issues.map((i) => (
            <p key={i.code} className={`flex items-start gap-1 ${i.level === "error" ? "text-red-700" : i.level === "warn" ? "text-amber-800" : "text-gray-600"}`}>
              {i.level === "error" ? <XCircle className="mt-px h-3.5 w-3.5 shrink-0" /> : i.level === "warn" ? <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" /> : <Info className="mt-px h-3.5 w-3.5 shrink-0" />}
              <span>{i.message}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
